import $ from "jquery";
import UI from "~/ui";
import { startCountdown } from "~/utils/CountdownTimer";
import { SchedaVenditaState } from "./SchedaVenditaState";

const POLLING_INTERVAL_MS = 5000;

interface StatoAstaDinamica {
    IsADInCorso: boolean;
    RichiestaAT: boolean;
    DataFineRaccoltaOfferte: string | null;
    Offerta: object | null;
}

/**
 * Manager della logica Asta Dinamica:
 * - avvio countdown se stato = 1 o 2
 * - toggle pulsanti (Fai un'offerta / Gestisci / Monitora) in base al ruolo
 *   e alla presenza di offerte via WebApi
 * - polling ogni 5 secondi per aggiornare i pulsanti
 */
export class AstaDinamicaManager {
    private readonly state: SchedaVenditaState;
    private pollingTimer: number | null = null;
    private countdownAvviato: boolean = false;

    constructor(state: SchedaVenditaState) {
        this.state = state;
    }

    public async init(): Promise<void> {
        // Prima chiamata immediata (avvia countdown se AD attiva), poi avvio polling
        await this.refreshDatiAstaDinamica();
        this.startPolling();
    }
    public startPolling(): void {
        if (this.pollingTimer !== null) return;
        this.pollingTimer = window.setInterval(async () => {
            await this.refreshDatiAstaDinamica();
        }, POLLING_INTERVAL_MS);
    }

    public stopPolling(): void {
        if (this.pollingTimer !== null) {
            window.clearInterval(this.pollingTimer);
            this.pollingTimer = null;
        }
    }

    private togglePulsanti(visibility: {
        btnMonitoraOfferte: boolean;
        btnGestisciOfferta: boolean;
        btnFaiUnOfferta: boolean;
    }): void {
        $("#btnMonitoraOfferte").toggleClass("d-none", !visibility.btnMonitoraOfferte);
        $("#btnGestisciOfferta").toggleClass("d-none", !visibility.btnGestisciOfferta);
        $("#btnFaiUnOfferta").toggleClass("d-none", !visibility.btnFaiUnOfferta);
    }

    private async refreshDatiAstaDinamica(): Promise<void> {
        const ui = this.state.userInfo;

        // L'endpoint richiede autenticazione: non chiamare la API se l'utente non è loggato
        if (!ui?.isAuthenticated || !ui?.anagraficaId) {
            const dataFineStr = $("#DataOraFineAstaDinamica").val() as string;
            const dataFine = dataFineStr ? this.parseDataFine(dataFineStr) : null;
            const astaScaduta = !dataFine || dataFine.getTime() <= Date.now();

            // Callback condiviso: nasconde il pulsante quando il countdown scade
            const onScaduto = () => this.togglePulsanti({ btnMonitoraOfferte: false, btnGestisciOfferta: false, btnFaiUnOfferta: false });

            if (dataFineStr) {
                this.avviaCountdown(dataFineStr, onScaduto);
            } else {
                console.error("Campo hidden #DataOraFineAstaDinamica non trovato o vuoto.");
            }

            this.togglePulsanti({ btnMonitoraOfferte: false, btnGestisciOfferta: false, btnFaiUnOfferta: !astaScaduta });
            this.nascondiLoader();
            this.stopPolling();
            return;
        }

        const userId = ui.anagraficaId;
        const url = `${window.BaseUrlWebApi}AstaDinamica/StatoAstaDinamica?venditaId=${this.state.venditaId}&userId=${userId}`;
        try {
            const data: StatoAstaDinamica = await this.state.auth.fetchWithAuth("GET " + url, {
                cache: "no-cache",
                headers: { "content-type": "application/json" },
            });
            this.visualizzaBoxAstaDinamica(data);
        } catch (error) {
            console.error("Errore nel refresh stato asta dinamica:", error);
            this.nascondiLoader();
        }
    }

    private nascondiLoader(): void {
        $("#adLoader").addClass("d-none");
        $("#adContent").removeClass("d-none");
    }

    private avviaCountdown(dataFineStr: string, onScaduto?: () => void): void {
        if (this.countdownAvviato) return;
        this.countdownAvviato = true;
        const dataFine = this.parseDataFine(dataFineStr);
        if (!dataFine) {
            console.error("Impossibile parsare la data di fine asta dinamica:", dataFineStr);
            return;
        }

        const now = new Date();
        const diffMs = dataFine.getTime() - now.getTime();
        if (diffMs <= 0) {
            $("#tempo").addClass("d-none");
            onScaduto?.();
            return;
        }

        const days    = Math.floor(diffMs / (24 * 60 * 60 * 1000));
        const hours   = Math.floor((diffMs % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
        const minutes = Math.floor((diffMs % (60 * 60 * 1000)) / (60 * 1000));
        const seconds = Math.floor((diffMs % (60 * 1000)) / 1000);

        const $countdown = $("#countdown");
        const formatTick = (r: { days: number; hours: number; minutes: number; seconds: number }) => {
            $countdown.text(
                `${String(r.days).padStart(2, "0")}g ` +
                `${String(r.hours).padStart(2, "0")}h ` +
                `${String(r.minutes).padStart(2, "0")}m ` +
                `${String(r.seconds).padStart(2, "0")}s`
            );
        };

        // Mostra subito il valore iniziale senza aspettare il primo tick
        formatTick({ days, hours, minutes, seconds });

        startCountdown(days, hours, minutes, seconds, formatTick, () => {
            $("#tempo").addClass("d-none");
            onScaduto?.();
        });
    }

    /** Parsa sia il formato italiano (dd/MM/yyyy HH:mm:ss) sia il formato ISO dalla API */
    private parseDataFine(dataFineStr: string): Date | null {
        // Prova prima il parser italiano (formato campo hidden: "dd/MM/yyyy HH:mm:ss")
        const dataItaliana = UI.parseDataItaliana(dataFineStr);
        if (dataItaliana) return dataItaliana;

        // Fallback: formato ISO 8601 restituito dalla API ("2026-07-30T17:25:00")
        const dataIso = new Date(dataFineStr);
        return isNaN(dataIso.getTime()) ? null : dataIso;
    }

    private avviaCountdownDaDom(): void {
        const dataFineStr = $("#DataOraFineAstaDinamica").val() as string;
        if (dataFineStr) {
            this.avviaCountdown(dataFineStr);
        } else {
            console.error("Campo hidden #DataOraFineAstaDinamica non trovato o vuoto.");
        }
    }

    private visualizzaBoxAstaDinamica(data: StatoAstaDinamica): void {
        const userId = parseInt(this.state.userInfo?.anagraficaId ?? "0");

        // Al primo caricamento: nasconde loader e mostra contenuto
        if (!this.countdownAvviato) {
            this.nascondiLoader();
        }

        if (!data.IsADInCorso) {
            // Asta terminata: fermo il polling, nascondo countdown e pulsanti
            this.stopPolling();
            $("#tempo").addClass("d-none");

            if (userId === this.state.venditoreId) {
                this.togglePulsanti({ btnMonitoraOfferte: true, btnGestisciOfferta: false, btnFaiUnOfferta: false });
                return;
            }

            const hasOfferta = data.Offerta !== null;
            this.togglePulsanti({
                btnMonitoraOfferte: false,
                btnGestisciOfferta: hasOfferta,
                btnFaiUnOfferta: false
            });

            return;
        }

        // Avvio countdown solo al primo refresh, quando l'asta è attiva
        if (!this.countdownAvviato) {
            if (data.DataFineRaccoltaOfferte) {
                this.avviaCountdown(data.DataFineRaccoltaOfferte);
            } else {
                // Fallback: usa il valore nel campo hidden
                this.avviaCountdownDaDom();
            }
        }


        if (userId === this.state.venditoreId) {
            this.togglePulsanti({ btnMonitoraOfferte: true, btnGestisciOfferta: false, btnFaiUnOfferta: false });
            return;
        }

        const hasOfferta = data.Offerta !== null;
        this.togglePulsanti({
            btnMonitoraOfferte: false,
            btnGestisciOfferta: hasOfferta,
            btnFaiUnOfferta: !hasOfferta
        });
    }
}
